import { put, take, takeLatest, all, fork } from 'redux-saga/effects'
import { requestActions } from 'state/request'
import { loading, success, failure } from 'utilities/actions'
import { stringify } from 'utilities/apiQueryString'
import * as actions from './actions'
import {
  API_URL,
  ORGANISATIONS_ENDPOINT,
  ORGANISATIONS_PAGE_SIZE
} from 'qap/constants'

import type { Organisation } from 'types'

// The /api/organisations endpoint is paginated and reports the grand total
// under `meta.total`, so we fetch a page at a time and accumulate until we have
// them all, then hand the full list to the reducer for local (client-side)
// filtering.
//
// Each page goes through AUTHED_REQUEST (rather than calling fetch directly) so
// 401/token-invalid responses are routed to expireSession and failures get the
// app's global error handling. AUTHED_REQUEST is fire-and-forget, so we await
// each page's resolution with `take` before requesting the next.
export function* fetchAllOrganisations(): Generator<any, any, any> {
  yield put({ type: loading(actions.API_ORGANISATIONS_ALL) })

  // The endpoint is scoped by the X-Organisation-Scope header (see api/fetch),
  // so record the scope this list was fetched under; the field refetches when
  // the scope changes.
  const scope = localStorage.getItem('organisation_ids') || ''

  let offset = 0
  let total = Infinity
  let all: Organisation[] = []

  while (all.length < total) {
    const queryString = stringify({ page: { offset, limit: ORGANISATIONS_PAGE_SIZE } })

    yield put({
      type: requestActions.AUTHED_REQUEST,
      method: 'GET',
      url: `${API_URL}/${ORGANISATIONS_ENDPOINT}?${queryString}`,
      cancelKey: 'organisations.all',
      hideLoading: true,
      successAction: success(actions.API_ORGANISATIONS_PAGE),
      failureAction: failure(actions.API_ORGANISATIONS_PAGE),
    })

    // Wait for this page to resolve. A token-invalid 401 never resolves here:
    // the request middleware dispatches expireSession instead, logging the user out.
    const response = yield take([
      success(actions.API_ORGANISATIONS_PAGE),
      failure(actions.API_ORGANISATIONS_PAGE),
    ])

    if (response.type === failure(actions.API_ORGANISATIONS_PAGE)) {
      yield put({
        type: failure(actions.API_ORGANISATIONS_ALL),
        errors: response.errors?.length ? response.errors : ['Failed to load'],
      })
      return
    }

    const pageData = response.data || []
    all = all.concat(pageData)
    total = response.meta?.total ?? all.length
    offset += ORGANISATIONS_PAGE_SIZE

    // Safety: never loop forever if a page comes back empty before the total.
    if (pageData.length === 0) break
  }

  yield put({ type: success(actions.API_ORGANISATIONS_ALL), data: all, scope })
}

function* watchFetchAllOrganisations(): Generator<any, any, any> {
  yield takeLatest([
    actions.API_ORGANISATIONS_ALL
  ], fetchAllOrganisations)
}

export default function* organisationsSagas(): Generator<any, any, any> {
  yield all([
    fork(watchFetchAllOrganisations)
  ])
}
